import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { Invite, Member } from '@fatigue/shared';
import { expiryLabel } from '@fatigue/shared';
import { inviteClinician, listClinicians, listInvites, suspendClinician } from '../../data/api';
import { InviteDialog } from '../InviteDialog';

/**
 * The account's people: who is in it, and who has been asked.
 *
 * Suspension is presented as suspension, never as deletion, because that is
 * what it is — the membership record survives, and it is what proves this
 * person was ever here. The button says so.
 */
export function Clinicians(): JSX.Element {
  const [members, setMembers] = useState<readonly Member[]>([]);
  const [invites, setInvites] = useState<readonly Invite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  /** Members whose suspension is still on its way, so a double click sends one. */
  const [suspending, setSuspending] = useState<ReadonlySet<string>>(new Set());

  const reload = (): Promise<void> =>
    Promise.all([listClinicians(), listInvites()])
      .then(([loadedMembers, loadedInvites]) => {
        setMembers(loadedMembers);
        setInvites(loadedInvites.filter((invite) => invite.kind === 'clinician'));
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not load the account.');
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    void reload();
  }, []);

  const suspend = (member: Member): void => {
    if (suspending.has(member.userId)) return;
    setSuspending((current) => new Set(current).add(member.userId));

    suspendClinician(member.userId)
      .then(reload)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not suspend that clinician.');
      })
      .finally(() =>
        setSuspending((current) => {
          const next = new Set(current);
          next.delete(member.userId);
          return next;
        }),
      );
  };

  const pending = invites.filter((invite) => invite.status === 'pending');
  const now = new Date();

  if (loading) return <p className="admin__note">Loading…</p>;

  return (
    <section className="admin__section">
      <header className="admin__head">
        <div>
          <h2 className="admin__title">Clinicians</h2>
          <p className="admin__sub">
            Everyone with a login in this organisation. A clinician sees a patient only once you
            assign them to that patient&rsquo;s cohort.
          </p>
        </div>
        <button type="button" className="primary" onClick={() => setInviting(true)}>
          Invite a clinician
        </button>
      </header>

      {error !== null && (
        <p className="admin__error" role="alert">
          {error}
        </p>
      )}

      <table className="admin__table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.userId}>
              <td>{member.displayName}</td>
              <td className="admin__mono">{member.email}</td>
              <td>{member.role === 'owner' ? 'Owner' : 'Clinician'}</td>
              <td>
                <span className={`pill pill--${member.status}`}>
                  {member.status === 'active' ? 'Active' : 'Suspended'}
                </span>
              </td>
              <td className="admin__actions">
                {/* The owner is not offered a suspend button for themselves:
                    an account with no owner has nobody who can invite, assign
                    or reinstate, and no route here creates a replacement. */}
                {member.role !== 'owner' && member.status === 'active' && (
                  <button
                    type="button"
                    className="ghost"
                    disabled={suspending.has(member.userId)}
                    onClick={() => suspend(member)}
                  >
                    {suspending.has(member.userId) ? 'Suspending…' : 'Suspend'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pending.length > 0 && (
        <>
          <h3 className="admin__subtitle">Awaiting acceptance</h3>
          <table className="admin__table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Sent</th>
                <th scope="col">Expires</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((invite) => (
                <tr key={invite.inviteId}>
                  <td className="admin__mono">{invite.email}</td>
                  <td>{new Date(invite.createdAt).toLocaleDateString('en-GB')}</td>
                  <td>{expiryLabel(invite.expiresAt, now)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <InviteDialog
        open={inviting}
        title="Invite a clinician"
        preamble="They will be emailed a link to set a password. Accepting puts them in this organisation with no patients — assign them to a cohort to give them a caseload."
        fields={[
          { name: 'displayName', label: 'Name' },
          {
            name: 'email',
            label: 'Email address',
            type: 'email',
            hint: 'Check this with them. The link it carries is the credential.',
          },
        ]}
        submitLabel="Send invitation"
        onSubmit={async (values) => {
          await inviteClinician({
            email: values['email'] ?? '',
            displayName: values['displayName'] ?? '',
          });
          void reload();
        }}
        onClose={() => setInviting(false)}
      />
    </section>
  );
}
